<template>
  <NebulaLayout v-if="user">
    <div class="studio-home">
      <HomeSidebar />
      <div class="studio-home__main">
        <div class="studio-home__header-wrapper">
          <h1 class="studio-home__header">
            {{ $t('Activity Templates') }}
          </h1>
        </div>
        <div class="studio-home__main-inner-wrap">
          <BoardFilters sidebar-filter="ACTIVITY_TEMPLATES">
            <div class="activity-templates__sos-banner">
              <NebulaIcon
                class="activity-templates__sos-icon"
                symbol-id="sos"
              />
              <h2 class="activity-templates__sos-heading">
                {{ $t('SOS Instructional Strategies') }}
              </h2>
            </div>
          </BoardFilters>
          <ol class="activity-templates__banner">
            <li class="activity-templates__banner-step">
              <div class="activity-templates__banner-numeral">
                1
              </div>
              <div class="activity-templates__banner-label">
                {{ $t('Pick Strategy Template') }}
              </div>
            </li>
            <li class="activity-templates__banner-step">
              <div class="activity-templates__banner-numeral">
                2
              </div>
              <div class="activity-templates__banner-label">
                {{ $t('Add Content') }}
              </div>
            </li>
            <li class="activity-templates__banner-step">
              <div class="activity-templates__banner-numeral">
                3
              </div>
              <div class="activity-templates__banner-label">
                {{ $t('Assign or Share with Students') }}
              </div>
            </li>
          </ol>
          <CometBlockMessage
            block-message-type="error"
            v-if="error"
          >
            <p>{{ error }}</p>
          </CometBlockMessage>
          <LoadingIndicator
            v-else-if="loading"
            class="studio-home__loading"
            :transparent="true"
          />
          <template v-else>
            <ActivityTemplatesList
              :templates="activityTemplates"
              :layout="query.layout"
            />
          </template>
        </div>
      </div>
    </div>
  </NebulaLayout>
</template>

<script>
import { get } from 'lodash-es';
import { mapState, mapActions } from 'pinia';
import { NebulaIcon } from '@discoveryedu/nebula-components';
import CometBlockMessage from '@/components/ui-elements/CometBlockMessage.vue';
import BoardFilters from '@/components/home/BoardFilters.vue';
import ActivityTemplatesList from '@/components/home/ActivityTemplatesList.vue';
import HomeSidebar from '@/components/home/HomeSidebar.vue';
import NebulaLayout from '@/components/navigation/NebulaLayout.vue';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import * as types from '@/lib/constants/store';
import {
  useHomeStore,
  useModalStore,
  useUserStore,
} from '@/stores';

export default {
  name: 'ActivityTemplates',
  components: {
    ActivityTemplatesList,
    BoardFilters,
    CometBlockMessage,
    HomeSidebar,
    LoadingIndicator,
    NebulaIcon,
    NebulaLayout,
  },
  computed: {
    ...mapState(useUserStore, [
      'userIsStudent',
      'userHasActivityTemplates',
      'user',
    ]),
    ...mapState(useHomeStore, [
      'activityTemplates',
      'loading',
      'error',
    ]),
    query() {
      return get(this, '$route.query', {});
    },
  },
  created() {
    this.getTemplates();

    if (this.user) {
      // Students do not have access to Activity Templates; send them back to Studio Home
      if (this.userIsStudent || !this.userHasActivityTemplates) {
        this.$router.push({ name: 'home' });
      }
    }

    if (this.$route.query.id) {
      this.openPreviewModal();
    }
  },
  watch: {
    $route: 'onRouteChange',
  },
  methods: {
    ...mapActions(useModalStore, [
      types.OPEN_MODAL,
    ]),
    ...mapActions(useHomeStore, [
      types.GET_ACTIVITY_TEMPLATES,
    ]),
    async getTemplates() {
      await this[types.GET_ACTIVITY_TEMPLATES]({
        query: this.query,
      });
      window.scrollTo(0, 0);
    },
    onRouteChange(to, from) {
      if (to.query.id && !from.query.id) {
        this.openPreviewModal();
      }
    },
    openPreviewModal() {
      this[types.OPEN_MODAL]({
        type: 'ActivityTemplateModal',
      });
    },
  },
};
</script>

<style lang="stylus">
@import '../styles/home.styl';

.activity-templates {
  &__sos-banner {
    align-items: center;
    display: flex;
    margin-block: 0 $nebula-space-1x;
    margin-inline: 0 auto;
  }

  &__sos-icon {
    fill: $nebula-color-interface-blue-600;
    margin-inline-end: $nebula-space-1x;
  }

  &__sos-heading {
    nebula-text-h6();
    margin: 0;
  }

  &__banner {
    nebula-text-body-1();
    background-color: $nebula-color-interface-blue-100;
    border-radius: $nebula-border-radius-badge-default;
    color: $nebula-color-interface-blue-600;
    display: flex;
    flex-wrap: wrap;
    margin: 0 0 $nebula-space-4x;
    padding: $nebula-space-2x $nebula-space-3x 0;
  }

  &__banner-step {
    align-items: center;
    display: flex;
    flex-wrap: nowrap;
    margin-bottom: $nebula-space-2x;

    &:not(:last-child) {
      margin-inline-end: $nebula-space-8x;
    }
  }

  &__banner-numeral {
    align-items: center;
    border: 1px solid $nebula-color-interface-blue-400;
    border-radius: 50%;
    display: flex;
    font-weight: bold;
    height: $nebula-space-5x;
    justify-content: center;
    width: $nebula-space-5x;
  }

  &__banner-label {
    padding-inline-start: $nebula-space-1x;
  }
}
</style>
